import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { accessibleModules, moduleItems } from "@/lib/modules";
import { ArrowRight } from "lucide-react";
import { useAppName } from "@/lib/branding";

export default function Launcher() {
  const appName = useAppName();
  const { can, user } = useAuth();
  const nav = useNavigate();
  const mods = accessibleModules(can);
  const firstName = (user?.name || "").split(" ")[0] || "there";

  const enter = (m) => {
    sessionStorage.setItem("activeModule", m.key);
    nav(moduleItems(m, can)[0].to);
  };

  // Single-module users skip the launcher entirely.
  useEffect(() => {
    if (mods.length === 1) {
      sessionStorage.setItem("activeModule", mods[0].key);
      nav(moduleItems(mods[0], can)[0].to, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mods.length <= 1) return null;

  return (
    <div className="min-h-[calc(100vh-3.5rem)] px-6 py-14 sm:py-20" data-testid="launcher">
      <div className="max-w-5xl mx-auto">
        <div className="mb-10 sm:mb-14">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{appName}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold font-brand text-slate-800">Welcome back, {firstName}.</h1>
          <p className="text-sm text-slate-500 mt-2">Choose a workspace to get started.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
          {mods.map((m, i) => (
            <button
              key={m.key}
              onClick={() => enter(m)}
              data-testid={`module-${m.key}`}
              className="group relative text-left rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)] hover:border-slate-300"
              style={{ animation: `launcher-in 0.4s ease both`, animationDelay: `${i * 60}ms` }}
            >
              <div
                className="h-14 w-14 rounded-xl flex items-center justify-center shadow-sm transition-transform duration-200 group-hover:scale-105"
                style={{ backgroundColor: m.accent }}
              >
                <m.icon className="h-7 w-7 text-white" strokeWidth={1.75} />
              </div>
              <div className="mt-4 text-base font-semibold text-slate-800 flex items-center gap-1.5">
                {m.label}
                <ArrowRight className="h-4 w-4 text-slate-300 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" style={{ color: m.accent }} />
              </div>
              <div className="text-xs text-slate-500 mt-1">{m.description}</div>
            </button>
          ))}
        </div>
      </div>

      <style>{`@keyframes launcher-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
